/**
 * Analytics Health Card Component
 *
 * Monitors the analytics post-processing pipeline and shows:
 * - Pipeline status (healthy/degraded/stale)
 * - Unprocessed transaction count and missing volume
 * - Last processed timestamp
 * - Manual reprocess button
 */

import React, { useEffect, useState, useCallback } from 'react';
import { getAnalyticsHealth, reprocessAnalytics } from '../../services/adminApi.js';
import type { AnalyticsHealthResponse } from '../../services/adminApi.js';

export function AnalyticsHealthCard() {
  const [health, setHealth] = useState<AnalyticsHealthResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadHealth = useCallback(async () => {
    try {
      setError(null);
      const response = await getAnalyticsHealth();
      setHealth(response.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
    const interval = setInterval(loadHealth, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [loadHealth]);

  const handleReprocess = async () => {
    if (!confirm('This will reprocess all unprocessed transactions from the last 7 days. Continue?')) {
      return;
    }

    setReprocessing(true);
    setReprocessResult(null);

    try {
      const response = await reprocessAnalytics();
      setReprocessResult(response.data.message);
      // Reload health after reprocessing
      await loadHealth();
    } catch (err: any) {
      setReprocessResult(`Error: ${err.message || 'Reprocessing failed'}`);
    } finally {
      setReprocessing(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'healthy':
        return {
          label: 'Healthy',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          textColor: 'text-green-800',
          badgeBg: 'bg-green-100',
          icon: '\u2713', // checkmark
        };
      case 'degraded':
        return {
          label: 'Degraded',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          textColor: 'text-yellow-800',
          badgeBg: 'bg-yellow-100',
          icon: '\u26A0', // warning
        };
      case 'stale':
        return {
          label: 'Stale — Volume Data Not Updating',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          textColor: 'text-red-800',
          badgeBg: 'bg-red-100',
          icon: '\u2717', // x mark
        };
      default:
        return {
          label: 'Unknown',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          textColor: 'text-gray-800',
          badgeBg: 'bg-gray-100',
          icon: '?',
        };
    }
  };

  const formatTimeAgo = (isoString: string | null) => {
    if (!isoString) return 'Never';
    const ms = Date.now() - new Date(isoString).getTime();
    const minutes = Math.round(ms / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    return `${days}d ago`;
  };

  if (loading && !health) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-56 mb-4"></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-24"></div>
              <div className="h-8 bg-gray-200 rounded w-20"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error && !health) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-red-900">Analytics Pipeline Health</h2>
        <p className="mt-2 text-sm text-red-700">{error}</p>
        <button
          onClick={loadHealth}
          className="mt-3 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!health) return null;

  const statusConfig = getStatusConfig(health.status);
  const isStale = health.status === 'stale';
  const isDegraded = health.status === 'degraded';
  const hasUnprocessed = health.unprocessed.recent7d > 0;

  return (
    <div className={`rounded-lg border ${isStale ? 'border-red-300 ring-2 ring-red-100' : isDegraded ? 'border-yellow-200' : 'border-gray-200'} bg-white p-4 sm:p-6 shadow-sm`}>
      {/* Header */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">Analytics Pipeline Health</h2>
        <span className={`rounded-full px-3 py-1 text-xs font-semibold w-fit ${statusConfig.badgeBg} ${statusConfig.textColor}`}>
          {statusConfig.icon} {statusConfig.label}
        </span>
      </div>

      {/* Stale warning banner */}
      {isStale && (
        <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3">
          <p className="text-sm text-red-800 font-medium">
            The analytics post-processor has stopped running. Volume data on the homepage and collection pages is stale.
            Use the Reprocess button below to fix immediately.
          </p>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4">
        {/* Unprocessed count */}
        <div className={`rounded-lg p-3 ${hasUnprocessed && isStale ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
          <div className="text-xs font-medium text-gray-500">Unprocessed (7d)</div>
          <div className={`text-xl sm:text-2xl font-bold ${hasUnprocessed && isStale ? 'text-red-700' : 'text-gray-900'}`}>
            {health.unprocessed.recent7d}
          </div>
          <div className="text-xs text-gray-500">transactions</div>
        </div>

        {/* Missing volume */}
        <div className={`rounded-lg p-3 ${health.unprocessed.missingVolumeXrp > 0 && isStale ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
          <div className="text-xs font-medium text-gray-500">Missing Volume</div>
          <div className={`text-xl sm:text-2xl font-bold ${health.unprocessed.missingVolumeXrp > 0 && isStale ? 'text-red-700' : 'text-gray-900'}`}>
            {health.unprocessed.missingVolumeXrp.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
          <div className="text-xs text-gray-500">XRP</div>
        </div>

        {/* Last processed */}
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-xs font-medium text-gray-500">Last Processed</div>
          <div className={`text-lg sm:text-xl font-bold ${isStale ? 'text-red-700' : 'text-gray-900'}`}>
            {formatTimeAgo(health.lastProcessedAt)}
          </div>
          <div className="text-xs text-gray-500">
            {health.processedLastHour > 0 ? `${health.processedLastHour} last hour` : 'idle'}
          </div>
        </div>

        {/* Latest analytics bucket */}
        <div className="rounded-lg bg-gray-50 p-3">
          <div className="text-xs font-medium text-gray-500">Latest Analytics</div>
          <div className="text-lg sm:text-xl font-bold text-gray-900">
            {formatTimeAgo(health.latestAnalyticsBucket)}
          </div>
          <div className="text-xs text-gray-500">hour bucket</div>
        </div>
      </div>

      {/* Action buttons — show when stale or there are unprocessed transactions */}
      {(isStale || hasUnprocessed) && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 rounded-md bg-gray-50 p-3">
          <button
            onClick={handleReprocess}
            disabled={reprocessing}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
              isStale
                ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
                : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400'
            } disabled:cursor-not-allowed`}
          >
            {reprocessing
              ? 'Reprocessing...'
              : hasUnprocessed
                ? `Reprocess ${health.unprocessed.recent7d} Transactions`
                : 'Reprocess Analytics'}
          </button>
          <span className="text-xs text-gray-500">
            Safe to run anytime — recalculates affected analytics buckets idempotently
          </span>
        </div>
      )}

      {/* Reprocess result */}
      {reprocessResult && (
        <div className={`mt-3 rounded-md p-3 text-sm ${
          reprocessResult.startsWith('Error')
            ? 'bg-red-50 text-red-700 border border-red-200'
            : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          {reprocessResult}
        </div>
      )}
    </div>
  );
}
